import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Bot, CheckCircle2, PlayCircle, XCircle } from 'lucide-react';
import type { DaemonRecord } from '../../generated/api/types.gen.js';
import { listDaemonsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Button, Callout, DataTable, EmptyState } from '../../components/ui/index.js';
import type { BadgeVariant, DataTableColumn } from '../../components/ui/index.js';
import { DashboardPage } from '../../components/page/DashboardPage.js';
import { PAGE_HELP } from '../../help/index.js';

const STATUS: Record<string, { label: string; variant: BadgeVariant }> = {
  running: { label: 'Running', variant: 'success' },
  error: { label: 'Error', variant: 'danger' },
};

const columns: DataTableColumn<DaemonRecord>[] = [
  {
    key: 'name',
    header: 'Daemon',
    sortable: true,
    sortValue: (d) => (d.name ?? '').toLowerCase(),
    render: (d) => (
      <div className="min-w-0">
        <Link
          to={`/admin/daemons/${d.id}`}
          className="block truncate text-sm font-medium text-heading hover:text-accent-text"
          onClick={(e) => e.stopPropagation()}
        >
          {d.name ?? `Daemon ${d.id}`}
        </Link>
        {d.type && <span className="block text-xs capitalize text-muted">{d.type}</span>}
      </div>
    ),
  },
  {
    key: 'runs',
    header: 'Runs',
    align: 'right',
    sortable: true,
    sortValue: (d) => d.runCount ?? 0,
    render: (d) => <span className="text-sm text-body">{d.runCount ?? 0}</span>,
  },
  {
    key: 'findings',
    header: 'Findings',
    align: 'right',
    sortable: true,
    sortValue: (d) => d.totalFindingsCount ?? 0,
    render: (d) => <span className="text-sm text-body">{d.totalFindingsCount ?? 0}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    render: (d) => {
      const status = STATUS[d.status ?? ''];
      return (
        <Badge variant={status?.variant ?? 'neutral'} dot>
          {status?.label ?? (d.status ? d.status.replace(/^\w/, (c) => c.toUpperCase()) : 'Idle')}
        </Badge>
      );
    },
  },
];

export const QualityDashboardPage: React.FC = () => {
  const help = PAGE_HELP['quality-dashboard'];
  const { data, isPending, isError, refetch } = useQuery(listDaemonsOptions());
  const daemons = data?.data ?? [];
  const errors = daemons.filter((d) => d.status === 'error').length;

  return (
    <DashboardPage
      title="Quality dashboard"
      subtitle="How the daemon fleet is running. Skill quality scores are computed daily at 2:00 UTC."
      helpContent={help}
      stats={
        isPending
          ? undefined
          : [
              { label: 'Daemons', value: daemons.length, icon: Bot },
              { label: 'Enabled', value: daemons.filter((d) => d.enabled).length, icon: CheckCircle2 },
              { label: 'Running', value: daemons.filter((d) => d.status === 'running').length, icon: PlayCircle },
              { label: 'In error', value: errors, icon: XCircle, severity: errors > 0 ? 'danger' : undefined },
            ]
      }
      alert={
        isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load quality data"
            actions={
              <Button size="sm" variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Check your connection and try again.
          </Callout>
        ) : undefined
      }
    >
      {!isError && (
        <DataTable
          framed
          columns={columns}
          rows={daemons}
          rowKey={(d) => d.id}
          loading={isPending}
          pageSize={25}
          aria-label="Daemon fleet"
          empty={
            <EmptyState
              icon={Bot}
              title={help.emptyState.title}
              description={help.emptyState.description}
              learnItems={help.emptyState.learnItems}
            />
          }
        />
      )}
    </DashboardPage>
  );
};
